/**
 * Centrale schema.org Service-catalogus.
 *
 * Eén bron van waarheid voor:
 * - Service JSON-LD per servicepagina
 * - OfferCatalog op de homepage
 * - CollectionPage/ItemList op overzichtpagina's
 *
 * Prijzen zijn vanaf-prijzen excl. BTW in EUR, zoals op de site getoond.
 * Routes zonder `fromPrice` krijgen wél een Service-entry, maar geen Offer
 * (Google laat deze dan vallen voor rich-results, prima — de Service blijft
 * zichtbaar als ondersteunende entiteit).
 *
 * Geen React-imports; dit bestand wordt ook tijdens build (sitemap) ingeladen.
 */

export type ServiceCategory =
  | "Internet"
  | "Telefonie"
  | "Mobiel"
  | "Werkplek"
  | "Hardware"
  | "Netwerken";

export type PriceUnit = "MONTH" | "USER_MONTH" | "ONE_TIME";

export interface ServiceEntry {
  /** URL-pad, exact gelijk aan de route in sitemap-routes.ts. */
  path: string;
  name: string;
  serviceType: string;
  description: string;
  category: ServiceCategory;
  fromPrice?: number;
  priceUnit?: PriceUnit;
}

export const ORG_ID = "https://rinqel.nl/#organization";
export const BASE_URL = "https://rinqel.nl";

export const serviceCatalog: ServiceEntry[] = [
  // /diensten/internet is een dynamische catch-all (geen eigen pagina).
  // We voeren alleen de concrete VDSL-pagina op in de catalog.
  {
    path: "/diensten/internet/vdsl",
    name: "Zakelijk VDSL",
    serviceType: "VDSL internet",
    description:
      "Betrouwbaar zakelijk VDSL via de telefoonlijn. Managed router en proactief netwerkbeheer inbegrepen.",
    category: "Internet",
    fromPrice: 55,
    priceUnit: "MONTH",
  },

  // Telefonie
  {
    path: "/diensten/telefonie",
    name: "Zakelijke telefonie",
    serviceType: "VoIP-telefonie",
    description:
      "Cloud-telefonie met centrale, keuzemenu's en CRM-koppeling. Vanaf €15 per maand per centrale plus €5 per gebruiker.",
    category: "Telefonie",
    fromPrice: 15,
    priceUnit: "MONTH",
  },
  {
    path: "/diensten/telefonie/functies",
    name: "Telefoniefuncties",
    serviceType: "Telefonie features",
    description:
      "Keuzemenu, wachtrij, doorschakelen, voicemail-naar-mail en CRM-koppeling. Altijd inbegrepen.",
    category: "Telefonie",
  },
  {
    path: "/diensten/telefonie/nummers",
    name: "Zakelijke telefoonnummers",
    serviceType: "Telefoonnummers",
    description:
      "Lokale nummers, 085-nummers en servicenummers. Inclusief gratis nummerportering.",
    category: "Telefonie",
  },
  {
    path: "/diensten/telefonie/overstappen",
    name: "Overstappen telefonie",
    serviceType: "Telefonie-migratie",
    description:
      "Naadloze overstap naar Rinqel-telefonie inclusief nummerportering en configuratie.",
    category: "Telefonie",
  },
  {
    path: "/diensten/telefonie/gesprekskosten",
    name: "Gesprekskosten",
    serviceType: "Telefonietarieven",
    description:
      "Transparante gesprekstarieven voor nationaal, mobiel en internationaal bellen.",
    category: "Telefonie",
  },

  // Mobiel
  {
    path: "/diensten/mobiel",
    name: "Zakelijk mobiel",
    serviceType: "Mobiele abonnementen",
    description:
      "Zakelijk mobiel via KPN, Odido of Vodafone. Eén aanspreekpunt, maandelijks opzegbaar.",
    category: "Mobiel",
  },
  {
    path: "/diensten/mobiel/kpn",
    name: "KPN zakelijk mobiel",
    serviceType: "Mobiele abonnementen (KPN)",
    description:
      "KPN Sim Only-abonnementen, data-only bundels en buitenlandbundels via Rinqel.",
    category: "Mobiel",
  },
  {
    path: "/diensten/mobiel/odido",
    name: "Odido zakelijk mobiel",
    serviceType: "Mobiele abonnementen (Odido)",
    description:
      "Odido Sim Only en Unlimited-abonnementen plus data-only bundels via Rinqel.",
    category: "Mobiel",
  },
  // Vodafone is in de UI tijdelijk verborgen, maar de schema-entry blijft staan
  // zodat de pagina bij her-activatie direct correct geïndexeerd wordt.
  {
    path: "/diensten/mobiel/vodafone",
    name: "Vodafone zakelijk mobiel",
    serviceType: "Mobiele abonnementen (Vodafone)",
    description:
      "Vodafone Sim Only-abonnementen en data-only bundels via Rinqel.",
    category: "Mobiel",
  },

  // Netwerken
  {
    path: "/diensten/netwerken",
    name: "Zakelijke netwerken",
    serviceType: "Netwerkbeheer",
    description:
      "Bekabeld en draadloos netwerk, switches, access points en firewalls — volledig beheerd.",
    category: "Netwerken",
  },

  // Werkplek
  {
    path: "/diensten/werkplek",
    name: "Digitale werkplek",
    serviceType: "Managed werkplek",
    description:
      "Complete Microsoft 365-werkplek met beveiliging en apparaatbeheer. Vanaf €25 per gebruiker per maand.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/cloud-only",
    name: "Managed Cloud Only werkplek",
    serviceType: "Cloud Only werkplek",
    description:
      "Werk overal veilig in de cloud met e-mail, Teams, OneDrive en SharePoint. Beheerd door Rinqel.",
    category: "Werkplek",
    fromPrice: 25,
    priceUnit: "USER_MONTH",
  },
  {
    path: "/diensten/werkplek/managed",
    name: "Managed werkplek",
    serviceType: "Managed werkplek (Premium)",
    description:
      "Microsoft 365 met beveiliging, Intune-apparaatbeheer en proactief beheer door Rinqel.",
    category: "Werkplek",
    fromPrice: 50,
    priceUnit: "USER_MONTH",
  },
  {
    path: "/diensten/werkplek/mobile-device",
    name: "Managed Mobile Device",
    serviceType: "Mobile Device Management",
    description:
      "Beheer zakelijke smartphones en tablets centraal en veilig via Intune.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/kiosk",
    name: "Managed Kiosk werkplek",
    serviceType: "Kiosk werkplek",
    description:
      "Vergrendelde werkplekken voor productie, recepties en publieke ruimtes.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/keeper",
    name: "Keeper Wachtwoordmanager",
    serviceType: "Wachtwoordmanagement",
    description:
      "Keeper Enterprise wachtwoordkluis met SSO via Microsoft 365.",
    category: "Werkplek",
    fromPrice: 6,
    priceUnit: "USER_MONTH",
  },
  {
    path: "/diensten/werkplek/emailhandtekeningen",
    name: "Professionele e-mailhandtekeningen",
    serviceType: "E-mailhandtekening-beheer",
    description:
      "Uniforme e-mailhandtekeningen voor je hele organisatie, centraal beheerd.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/windows",
    name: "Windows werkplek",
    serviceType: "Windows-werkplek",
    description:
      "Beheerde Windows-laptops en desktops met automatische updates en beveiliging.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/macos",
    name: "macOS werkplek",
    serviceType: "macOS-werkplek",
    description:
      "Beheerde Apple-werkplekken via Intune, volledig geïntegreerd in je zakelijke IT.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/exchange-online",
    name: "Exchange Online",
    serviceType: "Zakelijke e-mail",
    description:
      "Zakelijke e-mail met losse en gedeelde mailboxen, 50 GB opslag en back-up.",
    category: "Werkplek",
  },
  {
    path: "/diensten/werkplek/overstap",
    name: "Werkplek-overstap",
    serviceType: "IT-migratie",
    description:
      "Volledige IT-migratie zonder dataverlies of stilstand. Eenmalig €225 per werkplek.",
    category: "Werkplek",
    fromPrice: 225,
    priceUnit: "ONE_TIME",
  },

  // Hardware
  {
    path: "/diensten/hardware/inruilen",
    name: "Hardware inruilen",
    serviceType: "Hardware trade-in",
    description:
      "Ruil oude zakelijke devices in voor een vooraf afgesproken bedrag dat we verrekenen bij nieuwe hardware.",
    category: "Hardware",
  },
];

export const catalogByPath: Record<string, ServiceEntry> = Object.fromEntries(
  serviceCatalog.map((s) => [s.path, s]),
);

/** Bouw een Schema.org Service-blok voor één catalog-entry. */
export function buildServiceJsonLd(
  entry: ServiceEntry,
): Record<string, unknown> {
  const url = `${BASE_URL}${entry.path}`;
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    name: entry.name,
    serviceType: entry.serviceType,
    description: entry.description,
    url,
    provider: { "@id": ORG_ID },
    areaServed: { "@type": "Country", name: "Nederland" },
    category: entry.category,
  };

  if (typeof entry.fromPrice === "number") {
    base.offers = buildOffer(entry);
  }
  return base;
}

function buildOffer(entry: ServiceEntry): Record<string, unknown> {
  const url = `${BASE_URL}${entry.path}`;
  const price = entry.fromPrice!.toFixed(2);
  const isOneTime = entry.priceUnit === "ONE_TIME";

  const priceSpec: Record<string, unknown> = isOneTime
    ? {
        "@type": "PriceSpecification",
        price,
        priceCurrency: "EUR",
        valueAddedTaxIncluded: false,
        description: "Eenmalig per werkplek",
      }
    : {
        "@type": "UnitPriceSpecification",
        price,
        priceCurrency: "EUR",
        valueAddedTaxIncluded: false,
        unitCode: "MON",
        unitText:
          entry.priceUnit === "USER_MONTH"
            ? "per gebruiker per maand"
            : "per maand",
        referenceQuantity:
          entry.priceUnit === "USER_MONTH"
            ? {
                "@type": "QuantitativeValue",
                value: 1,
                unitText: "gebruiker",
              }
            : { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
      };

  return {
    "@type": "Offer",
    url,
    priceCurrency: "EUR",
    price,
    priceSpecification: priceSpec,
    availability: "https://schema.org/InStock",
    businessFunction: "https://schema.org/ProvideService",
    seller: { "@id": ORG_ID },
    eligibleRegion: { "@type": "Country", name: "Nederland" },
  };
}

/** OfferCatalog voor de homepage — gegroepeerd per categorie. */
export function buildHomeOfferCatalog(): Record<string, unknown> {
  const categories: ServiceCategory[] = [
    "Internet",
    "Telefonie",
    "Mobiel",
    "Werkplek",
    "Netwerken",
    "Hardware",
  ];

  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "@id": `${BASE_URL}/#catalog`,
    name: "Rinqel diensten",
    url: BASE_URL,
    provider: { "@id": ORG_ID },
    itemListElement: categories.map((cat) => ({
      "@type": "OfferCatalog",
      name: cat,
      itemListElement: serviceCatalog
        .filter((s) => s.category === cat)
        .map((s, i) =>
          typeof s.fromPrice === "number"
            ? {
                "@type": "Offer",
                url: `${BASE_URL}${s.path}`,
                price: s.fromPrice.toFixed(2),
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                itemOffered: { "@id": `${BASE_URL}${s.path}#service` },
              }
            : {
                // Service zonder vaste lijstprijs ('op aanvraag') → expose als
                // ListItem zodat de OfferCatalog geen Offer zonder prijs bevat
                // (verplicht veld in Google's Rich Results requirements).
                "@type": "ListItem",
                position: i + 1,
                url: `${BASE_URL}${s.path}`,
                item: { "@id": `${BASE_URL}${s.path}#service` },
              },
        ),
    })),
  };
}

/** CollectionPage + ItemList voor een overzichtpagina. */
export function buildCollectionPage(
  overviewPath: string,
  name: string,
  description: string,
  childPaths: string[],
): Record<string, unknown> {
  const url = `${BASE_URL}${overviewPath}`;
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name,
    description,
    inLanguage: "nl-NL",
    isPartOf: { "@id": `${BASE_URL}/#website` },
    about: { "@id": ORG_ID },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: childPaths.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${BASE_URL}${p}`,
        item: { "@id": `${BASE_URL}${p}#service` },
      })),
    },
  };
}
